import { useState, useEffect, useRef } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import FreePassScrollPopup from "@/components/layout/FreePassScrollPopup";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation, ExternalLink, ChevronLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL, analytics, getTodaysHours, addCacheBust } from "@/lib/utils";
import { getLocationIdFromSlug } from "@/lib/locationUrls";
import { SEO } from "@/components/SEO";

function PhotoGrid({ photos, locationName, onSelect }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div data-testid="photo-gallery">
      <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">PHOTO GALLERY</h2>
      <div className="gallery-grid">
        {photos.map((p, i) => (
          <div key={i} className="gallery-item cursor-pointer" onClick={() => onSelect(p)} data-testid={`gallery-image-${i}`}>
            <img 
              src={addCacheBust(p)} 
              alt={`${locationName} Photo ${i+1}`}
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800";
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function AmenitiesGrid({ amenities }) {
  if (!amenities || amenities.length === 0) return null;
  return (
    <div data-testid="amenities-section">
      <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">AMENITIES</h2>
      <div className="amenities-grid">
        {amenities.map((a, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-gray-100 border border-gray-200 rounded-lg" data-testid={`amenity-${i}`}>
            <Check className="w-5 h-5 text-brand-orange flex-shrink-0" />
            <span className="text-gray-800">{a}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoursTable({ hours, title }) {
  if (!hours || hours.length === 0) return null;
  return (
    <div className="bg-gray-100 border border-gray-200 rounded-lg p-6" data-testid="hours-section">
      <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">{title || "HOURS"}</h2>
      <div className="overflow-x-auto">
      <table className="hours-table">
        <tbody>
          {hours.map((h, i) => (
            <tr key={i}>
              <td className="day">{h.day}</td>
              <td className="hours">
                {h.is_closed ? <span className="text-red-400">Closed</span>
                  : h.open === "24 Hours" ? <span className="text-brand-orange">24 Hours</span>
                  : `${h.open} - ${h.close}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function PoolScheduleTable({ poolHours, notes }) {
  if (!poolHours || typeof poolHours !== "object") return null;

  const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const dayLabel = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  const rows = dayOrder
    .filter((day) => Array.isArray(poolHours[day]) && poolHours[day].length > 0)
    .map((day) => ({
      day: dayLabel[day],
      entries: poolHours[day].map((entry) => {
        if (typeof entry === "string") return entry;
        const marker = entry?.marker || "";
        if (!entry?.time || !entry?.label) return "";
        return `${marker}${entry.time} | ${entry.label}`;
      }).filter(Boolean),
    }));

  if (rows.length === 0) return null;

  const getEntryClasses = (entry) => {
    const lower = entry.toLowerCase();
    if (lower.includes("sj aquatics club")) return "text-blue-600";
    if (lower.includes("member swim")) return "text-brand-orange";
    return "text-gray-700";
  };

  return (
    <div className="bg-gray-100 border border-gray-200 rounded-lg p-6" data-testid="pool-schedule-section">
      <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Pool Schedule</h2>
      <div className="space-y-4">
        {rows.map((row) => (
          <section key={row.day} className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="text-lg font-heading font-bold text-gray-900 mb-3 normal-case">{row.day}</h3>
            <div className="space-y-2">
              {row.entries.map((line, i) => (
                <p key={`${row.day}-${i}`} className={`text-sm leading-relaxed break-words ${getEntryClasses(line)}`}>
                  {line}
                </p>
              ))}
            </div>
          </section>
        ))}
      </div>
      {Array.isArray(notes) && notes.length > 0 && (
        <div className="mt-5 border-t border-gray-200 pt-4 space-y-1">
          {notes.map((line, i) => {
            const isFirst = i === 0;
            const isDateList = i > 0;
            return (
              <p
                key={`pool-note-${i}`}
                className={`text-sm ${isFirst ? "italic text-blue-700" : isDateList ? "text-blue-600" : "text-gray-700"}`}
              >
                {line}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function LocationDetailPage() {
  const { locationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationPath = location.pathname;
  const [locationData, setLocationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(null);
  const [freePassOpen, setFreePassOpen] = useState(false);
  const popupTimerRef = useRef(null);

  useEffect(() => {
    // Handle both old format (/locations/:id) and new format (/:slug)
    let actualLocationId = locationId;
    
    if (locationPath.startsWith("/locations/")) {
      // Old format: /locations/:id - use locationId from params
      actualLocationId = locationId;
    } else if (!locationId) {
      // New format: /:slug - extract slug from path (no :locationId param)
      const slug = locationPath.replace(/^\/|\/$/g, "");
      actualLocationId = getLocationIdFromSlug(slug);
    }
    
    if (!actualLocationId) {
      navigate("/", { replace: true });
      return;
    }
    
    const url = `${API_URL}/locations/${actualLocationId}`;
    axios.get(url)
      .then(res => {
        setLocationData(res.data);
        analytics.trackLocationSelect(res.data.id, res.data.name);
      })
      .catch(err => {
        // Unknown location slug/id should go home instead of rendering a 404 page.
        if (err?.response?.status === 404) {
          navigate("/", { replace: true });
          return;
        }
        console.error("Failed:", err);
      })
      .finally(() => setIsLoading(false));
  }, [locationId, locationPath, navigate]);

  const POPUP_KEY = "gf_free_pass_shown";
  useEffect(() => {
    if (!locationData) return;
    if (localStorage.getItem(POPUP_KEY)) return;
    popupTimerRef.current = setTimeout(() => {
      setFreePassOpen(true);
      localStorage.setItem(POPUP_KEY, "1");
    }, 1500);
    return () => clearTimeout(popupTimerRef.current);
  }, [locationData]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <SEO
          title="Loading Location"
          description="Loading Giant Fitness location information..."
          url={locationPath}
          noindex={true}
        />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-orange" />
      </div>
    );
  }

  if (!locationData) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center" data-testid="location-not-found">
        <SEO
          title="Location Not Found"
          description="The requested Giant Fitness location could not be found. Browse all our locations."
          url={locationPath}
          noindex={true}
        />
        <MapPin className="w-16 h-16 text-brand-orange mb-4" />
        <h2 className="text-2xl font-heading text-gray-900 mb-2">Location Not Found</h2>
        <Link to="/locations"><Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">Back</Button></Link>
      </div>
    );
  }

  // Use first photo if available, otherwise fallback to placeholder
  const heroImg = (locationData.photos && locationData.photos.length > 0 && locationData.photos[0]) 
    ? locationData.photos[0] 
    : "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920";
  const handleSignup = () => {
    analytics.trackSignupClick(locationData.id, locationData.name);
    if (locationData.signup_url) {
      window.open(locationData.signup_url, "_blank", "noopener,noreferrer");
      navigate("/join-thank-you");
    }
  };

  const locationImage = locationData.photos && locationData.photos.length > 0 
    ? `https://giantfitnessclubs.com${locationData.photos[0]}` 
    : "https://giantfitnessclubs.com/images/GiantFitness-more-equipment-no-waiting-LOGO.png";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "HealthClub",
      "name": locationData.name,
      "description": `${locationData.name} - ${locationData.address}, ${locationData.city}, ${locationData.state}. Premium fitness center with ${locationData.amenities?.join(", ") || "state-of-the-art equipment"}.`,
      "image": locationImage,
      "telephone": locationData.phone,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": locationData.address,
        "addressLocality": locationData.city,
        "addressRegion": locationData.state,
        "postalCode": locationData.zip_code,
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": locationData.latitude,
        "longitude": locationData.longitude
      },
      "openingHoursSpecification": locationData.hours?.map(h => ({
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": h.day,
        "opens": h.open,
        "closes": h.close
      })) || [],
      "priceRange": "$$",
      "amenityFeature": locationData.amenities?.map(a => ({
        "@type": "LocationFeatureSpecification",
        "name": a,
        "value": true
      })) || []
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://giantfitnessclubs.com/" },
        { "@type": "ListItem", "position": 2, "name": "Locations", "item": "https://giantfitnessclubs.com/locations" },
        { "@type": "ListItem", "position": 3, "name": locationData.name, "item": `https://giantfitnessclubs.com${locationPath}` }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="location-detail-page">
      <SEO
        title={locationData.name}
        description={`${locationData.name} - ${locationData.address}, ${locationData.city}, ${locationData.state}. Premium fitness center with ${locationData.amenities?.slice(0, 3).join(", ") || "state-of-the-art equipment"}. ${locationData.is_women_only ? "Women-only facility." : ""} Sign up today!`}
        url={locationPath}
        image={locationImage}
        structuredData={structuredData}
      />
      <section className="relative h-64 md:h-96">
            <img 
              src={heroImg} 
              alt={locationData.name} 
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920";
              }}
            />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
        <div className="absolute top-4 left-4">
          <Link to="/locations"><Button variant="outline" size="sm" className="bg-black/50 border-white/20 text-white"><ChevronLeft className="w-4 h-4 mr-1" />All Locations</Button></Link>
        </div>
        {locationData.is_women_only && <div className="absolute top-4 right-4 women-only-badge">Women Only</div>}
        {locationData.id === "giant-fitness-mount-ephraim" && <div className="absolute top-4 right-4 giant-fitness-only-badge">Giant Fitness Only</div>}
      </section>

      <section className="px-4 md:px-8 -mt-20 relative z-10 pb-12">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-6 md:p-8 mb-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div>
                  <h1 className="text-3xl md:text-5xl font-heading font-black text-gray-900 mb-4" data-testid="location-name">{locationData.name}</h1>
                  {locationData.id === "giant-fitness-moorestown" && (
                    <p className="text-xl md:text-2xl font-heading font-black text-brand-orange mb-4" data-testid="moorestown-opening-tbd">OPENING DATE TBD</p>
                  )}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-gray-700"><MapPin className="w-5 h-5 text-brand-orange mt-0.5" /><span>{locationData.address}, {locationData.city}, {locationData.state} {locationData.zip_code}</span></div>
                    <div className="flex items-center gap-3 text-gray-700"><Phone className="w-5 h-5 text-brand-orange" /><a href={`tel:${locationData.phone}`} className="hover:text-brand-orange">{locationData.phone}</a></div>
                    <div className="flex items-center gap-3 text-gray-700"><Clock className="w-5 h-5 text-brand-orange" /><span>{locationData.hours?.length ? `Today: ${getTodaysHours(locationData.hours)}` : (locationData.hours_note || "Hours not available")}</span></div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
                  <Button size="lg" type="button" className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase" data-testid="signup-online-btn" onClick={handleSignup}>Sign Up Online<ExternalLink className="w-4 h-4 ml-2" /></Button>
                  <a href={locationData.directions_url} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="get-directions-btn"><Navigation className="w-4 h-4 mr-2" />Get Directions</Button></a>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <PhotoGrid photos={locationData.photos} locationName={locationData.name} onSelect={setSelectedImg} />
                <AmenitiesGrid amenities={locationData.amenities} />
              </div>
              <div className="space-y-8">
                <HoursTable hours={locationData.hours} title={locationData.hours_note === "Stand alone Jersey Girls Hours" ? "Hours" : locationData.hours_note} />
                <PoolScheduleTable poolHours={locationData.department_hours?.pool} notes={locationData.pool_schedule_notes} />
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-6" data-testid="quick-links">
                  <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">QUICK LINKS</h2>
                  <div className="space-y-2">
                    <Link to="/contact-us" className="block p-3 text-gray-700 hover:text-brand-orange rounded">Contact Us</Link>
                    <Link to="/feedback" className="block p-3 text-gray-700 hover:text-brand-orange rounded">Leave Feedback</Link>
                    <Link to="/cancel" className="block p-3 text-gray-700 hover:text-brand-orange rounded">Cancellation Request</Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <FreePassScrollPopup open={freePassOpen} onOpenChange={setFreePassOpen} />

      {selectedImg && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImg(null)}>
          <img 
            src={addCacheBust(selectedImg)} 
            alt="Gallery" 
            className="max-w-full max-h-full rounded"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200";
            }}
          />
        </div>
      )}
    </div>
  );
}
